import { compare, hash } from 'bcrypt';
import { sign, verify } from 'jsonwebtoken';
import { v4 as uuidv4 } from 'uuid';
import type {
  IAuthService,
  IAuthRepository,
  User,
  Session,
  TokenPayload
} from '../types';
import { AuthMethod, PermissionScope, PermissionAction } from '../types';

export class AuthService implements IAuthService {
  private readonly JWT_SECRET: string;
  private readonly SALT_ROUNDS = 10;
  private readonly SESSION_DURATION = 24 * 60 * 60 * 1000; // 24 hours
  private readonly REFRESH_TOKEN_DURATION = 30 * 24 * 60 * 60 * 1000; // 30 days
  private readonly MAX_FAILED_ATTEMPTS = 5;
  private readonly PIN_LENGTH = 6;

  constructor(
    private readonly authRepository: IAuthRepository,
    jwtSecret: string
  ) {
    this.JWT_SECRET = jwtSecret;
  }

  async createUser(userData: Omit<User, 'id' | 'createdAt' | 'updatedAt'>): Promise<User> {
    // Validate password complexity
    if (!this.validatePassword(userData.passwordHash)) {
      throw new Error('Password does not meet complexity requirements');
    }

    // Hash password
    const passwordHash = await hash(userData.passwordHash, this.SALT_ROUNDS);

    const user: User = {
      id: uuidv4(),
      ...userData,
      passwordHash,
      failedAttempts: 0,
      locked: false,
      active: true,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    return this.authRepository.createUser(user);
  }

  async updateUser(id: string, userData: Partial<User>): Promise<User> {
    const user = await this.authRepository.getUserById(id);
    if (!user) {
      throw new Error('User not found');
    }

    // If password is being updated, hash it
    if (userData.passwordHash) {
      if (!this.validatePassword(userData.passwordHash)) {
        throw new Error('Password does not meet complexity requirements');
      }
      userData.passwordHash = await hash(userData.passwordHash, this.SALT_ROUNDS);
    }

    const updatedUser: User = {
      ...user,
      ...userData,
      updatedAt: new Date()
    };

    return this.authRepository.updateUser(updatedUser);
  }

  async deleteUser(id: string): Promise<void> {
    // Invalidate all sessions first
    await this.invalidateAllSessions(id);
    await this.authRepository.deleteUser(id);
  }

  async getUserById(id: string): Promise<User | null> {
    return this.authRepository.getUserById(id);
  }

  async getUserByUsername(username: string): Promise<User | null> {
    return this.authRepository.getUserByUsername(username);
  }

  async login(username: string, password: string): Promise<Session> {
    const user = await this.authRepository.getUserByUsername(username);
    if (!user) {
      throw new Error('Invalid credentials');
    }

    // Check if account is locked or inactive
    if (user.locked || !user.active) {
      throw new Error('Account is locked or inactive');
    }

    // Verify password
    const isValid = await compare(password, user.passwordHash);
    if (!isValid) {
      // Increment failed attempts
      const failedAttempts = user.failedAttempts + 1;
      const locked = failedAttempts >= this.MAX_FAILED_ATTEMPTS;

      await this.updateUser(user.id, {
        failedAttempts,
        locked,
        updatedAt: new Date()
      });

      throw new Error('Invalid credentials');
    }

    // Reset failed attempts on successful login
    if (user.failedAttempts > 0) {
      await this.updateUser(user.id, {
        failedAttempts: 0,
        updatedAt: new Date()
      });
    }

    // Create session
    return this.createSession(user);
  }

  async loginWithPin(username: string, pin: string): Promise<Session> {
    const user = await this.authRepository.getUserByUsername(username);
    if (!user || !user.pin || !user.authMethods.includes(AuthMethod.PIN)) {
      throw new Error('PIN authentication not available');
    }

    // Verify PIN
    const isValid = await compare(pin, user.pin);
    if (!isValid) {
      throw new Error('Invalid PIN');
    }

    return this.createSession(user);
  }

  async loginWithBiometric(username: string, biometricToken: string): Promise<Session> {
    const user = await this.authRepository.getUserByUsername(username);
    if (!user || !user.authMethods.includes(AuthMethod.BIOMETRIC)) {
      throw new Error('Biometric authentication not available');
    }

    // Verify biometric token (implementation depends on biometric system used)
    // This is a placeholder for actual biometric verification
    if (!this.verifyBiometricToken(biometricToken)) {
      throw new Error('Invalid biometric token');
    }

    return this.createSession(user);
  }

  async logout(sessionId: string): Promise<void> {
    await this.invalidateSession(sessionId);
  }

  async refreshSession(refreshToken: string): Promise<Session> {
    try {
      // Verify refresh token
      const payload = await verify(refreshToken, this.JWT_SECRET) as TokenPayload;
      const session = await this.authRepository.getSessionById(payload.sessionId);

      if (!session || !session.active || session.refreshToken !== refreshToken) {
        throw new Error('Invalid refresh token');
      }

      const user = await this.authRepository.getUserById(payload.userId);
      if (!user || !user.active) {
        throw new Error('User not found or inactive');
      }

      // Create new session
      return this.createSession(user);
    } catch (error) {
      throw new Error('Invalid refresh token');
    }
  }

  async setupPin(userId: string, pin: string): Promise<void> {
    if (!this.validatePin(pin)) {
      throw new Error('Invalid PIN format');
    }

    const user = await this.authRepository.getUserById(userId);
    if (!user) {
      throw new Error('User not found');
    }

    const pinHash = await hash(pin, this.SALT_ROUNDS);
    await this.updateUser(userId, {
      pin: pinHash,
      authMethods: [...new Set([...user.authMethods, AuthMethod.PIN])],
    });
  }

  async changePin(userId: string, currentPin: string, newPin: string): Promise<void> {
    const user = await this.authRepository.getUserById(userId);
    if (!user || !user.pin) {
      throw new Error('User not found or PIN not set');
    }

    // Verify current PIN
    const isValid = await compare(currentPin, user.pin);
    if (!isValid) {
      throw new Error('Invalid current PIN');
    }

    await this.setupPin(userId, newPin);
  }

  async resetPin(userId: string): Promise<string> {
    const user = await this.authRepository.getUserById(userId);
    if (!user) {
      throw new Error('User not found');
    }

    // Generate new PIN
    const newPin = this.generatePin();
    await this.setupPin(userId, newPin);

    return newPin;
  }

  async enableBiometric(userId: string): Promise<string> {
    const user = await this.authRepository.getUserById(userId);
    if (!user) {
      throw new Error('User not found');
    }

    // Generate biometric registration token
    const registrationToken = this.generateBiometricToken();

    await this.updateUser(userId, {
      authMethods: [...new Set([...user.authMethods, AuthMethod.BIOMETRIC])],
    });

    return registrationToken;
  }

  async disableBiometric(userId: string): Promise<void> {
    const user = await this.authRepository.getUserById(userId);
    if (!user) {
      throw new Error('User not found');
    }

    await this.updateUser(userId, {
      authMethods: user.authMethods.filter(m => m !== AuthMethod.BIOMETRIC),
    });
  }

  async getActiveSessions(userId: string): Promise<Session[]> {
    const sessions = await this.authRepository.getSessionsByUserId(userId);
    return sessions.filter(s => s.active && s.expiresAt > new Date());
  }

  async invalidateSession(sessionId: string): Promise<void> {
    const session = await this.authRepository.getSessionById(sessionId);
    if (session) {
      session.active = false;
      session.updatedAt = new Date();
      await this.authRepository.updateSession(session);
    }
  }

  async invalidateAllSessions(userId: string): Promise<void> {
    await this.authRepository.deleteSessionsByUserId(userId);
  }

  validatePassword(password: string): boolean {
    // Password requirements:
    // - At least 8 characters
    // - At least one uppercase letter
    // - At least one lowercase letter
    // - At least one number
    // - At least one special character
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    return passwordRegex.test(password);
  }

  validatePin(pin: string): boolean {
    return pin.length === this.PIN_LENGTH && /^\d+$/.test(pin);
  }

  async isSessionValid(sessionId: string): Promise<boolean> {
    const session = await this.authRepository.getSessionById(sessionId);
    return !!(session && session.active && session.expiresAt > new Date());
  }

  async hasPermission(
    userId: string,
    scope: PermissionScope,
    action: PermissionAction
  ): Promise<boolean> {
    const user = await this.authRepository.getUserById(userId);
    if (!user) {
      return false;
    }

    // Check if user has the required permission
    return user.permissions.some(
      p => p.scope === scope && p.actions.includes(action)
    );
  }

  private async createSession(user: User): Promise<Session> {
    const sessionId = uuidv4();
    const expiresAt = new Date(Date.now() + this.SESSION_DURATION);

    const tokenPayload: TokenPayload = {
      userId: user.id,
      username: user.username,
      role: user.role,
      permissions: user.permissions,
      sessionId
    };

    const token = await sign(tokenPayload, this.JWT_SECRET, {
      expiresIn: this.SESSION_DURATION / 1000
    });

    const refreshToken = await sign(
      { userId: user.id, sessionId },
      this.JWT_SECRET,
      { expiresIn: this.REFRESH_TOKEN_DURATION / 1000 }
    );

    const session: Session = {
      id: sessionId,
      userId: user.id,
      token,
      refreshToken,
      expiresAt,
      lastActivity: new Date(),
      deviceInfo: this.getDeviceInfo(),
      active: true,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    return this.authRepository.createSession(session);
  }

  private getDeviceInfo() {
    // In a real implementation, this would get actual device info
    return {
      id: uuidv4(),
      name: 'Unknown Device',
      platform: 'web'
    };
  }

  private generatePin(): string {
    return Array.from(
      { length: this.PIN_LENGTH },
      () => Math.floor(Math.random() * 10)
    ).join('');
  }

  private generateBiometricToken(): string {
    // In a real implementation, this would generate a proper biometric registration token
    return uuidv4();
  }

  private verifyBiometricToken(token: string): boolean {
    // In a real implementation, this would verify the biometric token
    return true;
  }
}